// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import classNames from "classnames";

import { Icons } from "@components";
import { getFormattedHoursMinutesSeconds } from "@transformers/date";
import accountActionCreator from "@modules/account/actionCreator";
import * as accountSelectors from "@modules/account/selectors";
import type { State } from "@store/reducers";

import styles from "./session-timer.scss";

const MINS_5_IN_MS = 300000;
const MINS_1_IN_MS = 60000;
const AUTO_RESET_INTERVAL_IN_MS = MINS_5_IN_MS + MINS_1_IN_MS;

type PropsFromState = $Exact<{
  tokenExpiresAt: string,
}>;

type PropsFromDispatch = $Exact<{
  extendLogin: typeof accountActionCreator.CheckLogin,
}>;

type Props = $Exact<{
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type SessionTimerState = $Exact<{
  formattedTime: string,
  currentLevel: number,
  autoResetTriggersAdded: boolean,
}>;

export class SessionTimer extends React.Component<Props, SessionTimerState> {
  interval: IntervalID;

  initialState = {
    formattedTime: "",
    currentLevel: 0,
    autoResetTriggersAdded: false,
  };

  state = this.initialState;

  componentDidMount() {
    this.interval = setInterval(this.update, 1000);
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.tokenExpiresAt !== this.props.tokenExpiresAt) {
      this.setState(this.initialState);
    }
  }

  componentWillUnmount() {
    clearInterval(this.interval);
    this.removeAutoResetTriggers();
  }

  update = () => {
    const timeToExpiryInMs = new Date(this.props.tokenExpiresAt).getTime() - new Date().getTime();
    let formattedTime = getFormattedHoursMinutesSeconds(timeToExpiryInMs);

    if (timeToExpiryInMs <= 0) {
      formattedTime = "00:00:00";
      this.props.extendLogin();
    }

    let nextLevel = 0;

    if (timeToExpiryInMs - AUTO_RESET_INTERVAL_IN_MS < 0) {
      this.addAutoResetTriggers();
    }

    if (timeToExpiryInMs - MINS_1_IN_MS < 0) {
      nextLevel = 2;
    } else if (timeToExpiryInMs - MINS_5_IN_MS < 0) {
      nextLevel = 1;
    }

    this.setState({ formattedTime, currentLevel: nextLevel });
  };

  extend = () => {
    this.removeAutoResetTriggers();
    this.props.extendLogin();
  };

  removeAutoResetTriggers = () => {
    document.removeEventListener("mousemove", this.extend);
    document.removeEventListener("keyup", this.extend);
  };

  addAutoResetTriggers = () => {
    if (this.state.autoResetTriggersAdded) {
      return;
    }
    document.addEventListener("mousemove", this.extend);
    document.addEventListener("keyup", this.extend);
    this.setState({ autoResetTriggersAdded: true });
  };

  render() {
    return (
      <div
        className={classNames("session-timer__container", {
          "session-timer__container--active": this.state.currentLevel > 0,
          "session-timer__container--warn": this.state.currentLevel === 1,
          "session-timer__container--danger": this.state.currentLevel === 2,
        })}
        onClick={this.extend}
      >
        <div className="session-timer__timer">
          {this.props.t("SESSION_EXPIRES_IN_FORMATTED_TIME", {
            formattedTime: this.state.formattedTime,
          })}
        </div>
        <div className="session-timer__close">
          <Icons.CloseIcon color="white" />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    tokenExpiresAt: accountSelectors.getTokenExpiresAt(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  extendLogin: accountActionCreator.CheckLogin,
};

export default compose(connect(mapStateToProps, mapDispatchToProps), localize())(SessionTimer);
